
import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

interface FeedbackEmojisProps {
  onFeedback: (feedbackType: "positive" | "neutral" | "negative") => void;
  className?: string;
}

const FeedbackEmojis: React.FC<FeedbackEmojisProps> = ({ onFeedback, className }) => {
  const { darkMode } = useSettings();

  const emojiOptions = [
    { value: "positive", emoji: "👍", label: "Helpful" },
    { value: "neutral", emoji: "😐", label: "Somewhat" },
    { value: "negative", emoji: "👎", label: "Not really" },
  ];
  
  const handleFeedback = (feedbackType: "positive" | "neutral" | "negative") => {
    // Call the parent callback
    onFeedback(feedbackType);
    
    // Show a toast notification based on the feedback type
    const messages = {
      positive: "Thanks! Glad it was helpful.",
      neutral: "Thanks for your feedback. We'll keep improving.",
      negative: "Thanks for letting us know. We'll try to do better."
    };
    
    toast(messages[feedbackType], {
      duration: 3000,
      position: "bottom-center",
    });
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className={cn("text-center space-y-3", className)}
    >
      <p className={cn(
        "text-sm font-medium", 
        darkMode ? "text-[#FBF3F1]" : "text-gray-700"
      )}>
        Did you find a helpful next step?
      </p>
      
      <div className="flex justify-center space-x-6">
        {emojiOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => handleFeedback(option.value as "positive" | "neutral" | "negative")}
            className="flex flex-col items-center transition-transform hover:scale-110"
          >
            <span className="text-2xl mb-1">{option.emoji}</span>
            <span className={cn(
              "text-xs", 
              darkMode ? "text-[#FBF3F1]/70" : "text-gray-600"
            )}>
              {option.label}
            </span>
          </button>
        ))}
      </div>
    </motion.div>
  );
};

export default FeedbackEmojis;
